import { defineStore } from 'pinia';

/**
 * Settings store - manages application settings and preferences
 */
export const useSettingsStore = defineStore('settings', {
  // State
  state: () => ({
    theme: localStorage.getItem('theme') || 'light',
    sidebarCollapsed:
      localStorage.getItem('sidebarCollapsed') === 'true' || false,
    layoutType: localStorage.getItem('layoutType') || 'vertical',
    language: localStorage.getItem('language') || 'en',
    direction: localStorage.getItem('direction') || 'ltr',
    isFixedWidth: localStorage.getItem('isFixedWidth') === 'true' || false,
    isSidebarHidden:
      localStorage.getItem('isSidebarHidden') === 'true' || false,
    isMobileSidebarActive: false,
    isLoading: false,
    error: null,
    systemSettings: {
      companyName: '',
      companyLogo: '',
      contactEmail: '',
      contactPhone: '',
      address: '',
      currency: 'USD',
      dateFormat: 'YYYY-MM-DD',
      timeFormat: '24h',
    },
  }),

  // Getters
  getters: {
    isDarkMode: (state) => state.theme === 'dark',
    isRtl: (state) => state.direction === 'rtl',
    currentLanguage: (state) => state.language,
    currentLayoutType: (state) => state.layoutType,
  },

  // Actions
  actions: {
    // Set theme (light/dark)
    setTheme(theme) {
      this.theme = theme;
      localStorage.setItem('theme', theme);
      document.documentElement.setAttribute('data-bs-theme', theme);
    },

    // Toggle dark mode
    toggleDarkMode() {
      const newTheme = this.theme === 'light' ? 'dark' : 'light';
      this.setTheme(newTheme);
    },

    // Set layout type
    setLayoutType(layoutType) {
      this.layoutType = layoutType;
      localStorage.setItem('layoutType', layoutType);
    },

    // Set language
    setLanguage(language) {
      this.language = language;
      localStorage.setItem('language', language);
    },

    // Set direction (ltr/rtl)
    setDirection(direction) {
      this.direction = direction;
      localStorage.setItem('direction', direction);
      document.documentElement.setAttribute('dir', direction);
    },

    // Toggle sidebar collapsed state
    toggleSidebar() {
      this.sidebarCollapsed = !this.sidebarCollapsed;
      localStorage.setItem('sidebarCollapsed', this.sidebarCollapsed);
    },

    // Toggle visibility of mobile sidebar
    toggleMobileSidebar() {
      this.isMobileSidebarActive = !this.isMobileSidebarActive;
    },

    // Set full width layout
    setFullWidth() {
      this.isFixedWidth = false;
      localStorage.setItem('isFixedWidth', 'false');
    },

    // Set fixed width layout
    setFixedWidth() {
      this.isFixedWidth = true;
      localStorage.setItem('isFixedWidth', 'true');
    },

    // Toggle sidebar hidden state
    toggleSidebarVisibility() {
      this.isSidebarHidden = !this.isSidebarHidden;
      localStorage.setItem('isSidebarHidden', this.isSidebarHidden);
    },

    // Fetch system settings from backend
    async fetchSystemSettings() {
      this.isLoading = true;
      try {
        // Here would be the API call
        // Example: const response = await api.get('/settings')
        // this.systemSettings = response.data

        // Placeholder implementation
        setTimeout(() => {
          this.systemSettings = {
            companyName: 'Confeccao Digital',
            companyLogo: '/logo.png',
            contactEmail: 'contato@fornecedoresconfeccao.com.br',
            contactPhone: '',
            address: '',
            currency: 'BRL',
            dateFormat: 'YYYY-MM-DD',
            timeFormat: '24h',
          };
          this.error = null;
          this.isLoading = false;
        }, 500);
      } catch (err) {
        this.error = err.message || 'Failed to fetch system settings';
        this.isLoading = false;
      }
    },

    // Update system settings
    async updateSystemSettings(settings) {
      this.isLoading = true;
      try {
        // Here would be the API call
        // Example: const response = await api.put('/settings', settings)

        // Placeholder implementation
        this.systemSettings = {
          ...this.systemSettings,
          ...settings,
        };
        this.error = null;
        return this.systemSettings;
      } catch (err) {
        this.error = err.message || 'Failed to update system settings';
        throw new Error(this.error);
      } finally {
        this.isLoading = false;
      }
    },

    // Initialize settings from localStorage and apply them
    initializeSettings() {
      // Apply theme
      document.documentElement.setAttribute('data-bs-theme', this.theme);

      // Apply direction
      document.documentElement.setAttribute('dir', this.direction);

      // Fetch system settings
      this.fetchSystemSettings();
    },
  },
});
